import { EventType } from "@/analytics/analytics-events";
import {
  useIsKeyPressed,
  useKeyboardEvents,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { LENS_CENTER, LensNoCircle } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OrthographicCamera, Vector2, Vector3 } from "three";

/** Utilities, states and callbacks to communicate with the interaction logic. */
export type InteractionLogic = {
  /** The start point of the scaling segment, in 3D coordinates */
  startPoint: Vector3 | undefined;

  /** The end point of the scaling segment, in 3D coordinates */
  endPoint: Vector3 | undefined;

  /** Callback to run when the pointer is pressed */
  onPointerDown(ev: ThreeEvent<PointerEvent>): void;

  /** Callback to run when the pointer is released */
  onPointerUp(ev: ThreeEvent<PointerEvent>): void;

  /** Callback to run when the pointer is moved */
  onPointerMove(ev: ThreeEvent<PointerEvent>): void;

  /** Callback to run when the user removes the scaling measurement */
  onMeasureRemoved(): void;

  /** Callback to run when the user undoes the removal of the scaling measurement */
  onUndoMeasureRemoval(): void;

  /** Whether the pointer controls to move the floorplan are enabled */
  controlsEnabled: boolean;

  /** Whether the measurement line is being dragged */
  isDraggingMeasureLine: boolean;
};

type InteractionLogicProps = {
  /** Callback to run when the user completes a measurement */
  onMeasurementCreated(distance: number): void;
  /** Callback issue when the first point is placed, to reset any previously set distance value */
  resetDistance(): void;
};

/** @returns pointer interaction logic for the floor scale mode */
export function useInteractionLogic({
  onMeasurementCreated,
  resetDistance,
}: InteractionLogicProps): InteractionLogic {
  const keyboardEvents = useKeyboardEvents(document.body);
  const isShiftPressed = useIsKeyPressed(keyboardEvents, "Shift");

  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);

  assert(camera instanceof OrthographicCamera);

  // Start and end points of the defined line
  const [startPoint, setStartPoint] = useState<Vector3>();
  const [endPoint, setEndPoint] = useState<Vector3>();

  // Current interaction state.
  const [interactionState, setInteractionState] = useState<InteractionState>(
    InteractionState.NoPointPlaced,
  );

  // Show custom cursor while drawing the line
  useEffect(() => {
    const noCursor = interactionState === InteractionState.SecondPointPlaced;

    // When the user has already a valid line show the default cursor
    if (noCursor) {
      return;
    }

    // Use a custom cursor while the line is not yet defined
    const originalCursor = document.body.style.cursor;
    document.body.style.cursor = `url("${LensNoCircle}") ${LENS_CENTER} ${LENS_CENTER},auto`;

    return () => {
      document.body.style.cursor = originalCursor;
    };
  }, [interactionState]);

  const onClick = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      ev.stopPropagation();
      switch (interactionState) {
        case InteractionState.NoPointPlaced: {
          setStartPoint(ev.point);
          setInteractionState(InteractionState.FirstPointPlaced);
          resetDistance();
          break;
        }
        case InteractionState.FirstPointPlaced: {
          assert(
            startPoint,
            "In FirstPointPlaced, startPoint should be defined",
          );
          setInteractionState(InteractionState.SecondPointPlaced);
          const { adjustedEnd } = computeMeasureEnd(
            startPoint,
            ev.point,
            camera,
            isShiftPressed,
          );
          setEndPoint(adjustedEnd);
          lastCursorRef.current = null;

          const distance = adjustedEnd.distanceTo(startPoint);
          onMeasurementCreated(distance);
          break;
        }
        case InteractionState.MovingEndPoint:
        case InteractionState.MovingStartPoint: {
          lastCursorRef.current = null;
          Analytics.track(EventType.adjustAreaScaleLine);
          setInteractionState(InteractionState.SecondPointPlaced);
          setControlsEnabled(true);
          break;
        }
      }
    },
    [
      interactionState,
      resetDistance,
      startPoint,
      onMeasurementCreated,
      camera,
      isShiftPressed,
    ],
  );

  const onDragRelease = useCallback(() => {
    switch (interactionState) {
      case InteractionState.MovingEndPoint:
      case InteractionState.MovingStartPoint:
        setInteractionState(InteractionState.SecondPointPlaced);
        setControlsEnabled(true);
        break;
    }
  }, [interactionState]);

  const pointerDownCoords = useMemo(() => new Vector2(), []);

  // Last raw cursor position, used to recompute snapping when Shift toggles
  const lastCursorRef = useRef<Vector3 | null>(null);

  const [isPointerDown, setIsPointerDown] = useState<boolean>(false);

  const [controlsEnabled, setControlsEnabled] = useState<boolean>(true);

  const onPointerDown = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      pointerDownCoords.set(ev.clientX, ev.clientY);
      setIsPointerDown(true);

      switch (interactionState) {
        case InteractionState.HoveringEndPoint: {
          setInteractionState(InteractionState.MovingEndPoint);
          setControlsEnabled(false);
          break;
        }
        case InteractionState.HoveringStartPoint: {
          setInteractionState(InteractionState.MovingStartPoint);
          setControlsEnabled(false);
          break;
        }
      }
    },
    [pointerDownCoords, interactionState],
  );

  const onPointerUp = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      setIsPointerDown(false);
      const isClick =
        Math.abs(pointerDownCoords.x - ev.clientX) +
          Math.abs(pointerDownCoords.y - ev.clientY) <
        5;
      if (isClick) {
        onClick(ev);
      } else {
        onDragRelease();
      }
    },
    [pointerDownCoords, onClick, onDragRelease],
  );

  const moveEndPoint = useCallback(
    (point: Vector3, snapToAxis: boolean) => {
      assert(startPoint, "Now the start point should be defined");
      const { adjustedEnd } = computeMeasureEnd(
        startPoint,
        point,
        camera,
        snapToAxis,
      );
      setEndPoint(adjustedEnd);
    },
    [startPoint, camera],
  );

  const moveStartPoint = useCallback(
    (point: Vector3, snapToAxis: boolean) => {
      assert(endPoint, "In this moment the end point should be defined");
      const { adjustedEnd } = computeMeasureEnd(
        endPoint,
        point,
        camera,
        snapToAxis,
      );
      setStartPoint(adjustedEnd);
    },
    [endPoint, camera],
  );

  const checkHoveringStarted = useCallback(
    (pointerCoord: Vector3) => {
      if (isPointerDown) return;
      assert(startPoint);
      assert(endPoint);
      // check whether the pointer is hovering the endpoints
      if (isHovering(startPoint, pointerCoord, camera, size.height)) {
        setInteractionState(InteractionState.HoveringStartPoint);
      } else if (isHovering(endPoint, pointerCoord, camera, size.height)) {
        setInteractionState(InteractionState.HoveringEndPoint);
      }
    },
    [isPointerDown, camera, size, startPoint, endPoint],
  );

  const checkHoveringEnded = useCallback(
    (point: Vector3 | undefined, pointerCoord: Vector3) => {
      assert(point);
      if (
        !isPointerDown &&
        !isHovering(point, pointerCoord, camera, size.height)
      ) {
        setInteractionState(InteractionState.SecondPointPlaced);
      }
    },
    [isPointerDown, camera, size],
  );

  /** Update the line end point based on the cursor movements during live editing */
  const onPointerMove = useCallback(
    (ev: ThreeEvent<PointerEvent>) => {
      ev.stopPropagation();
      // Handling here the case in which the pointer down event was propagated here but the
      // pointer up event wasn't, maybe because it was issed on the toolbar or on another component
      const isDown =
        ev.pointerType === "touch" ||
        (ev.pointerType === "mouse" && ev.buttons !== 0);
      if (isDown !== isPointerDown) {
        if (isDown) onPointerDown(ev);
        else onPointerUp(ev);
        return;
      }

      switch (interactionState) {
        case InteractionState.FirstPointPlaced:
          lastCursorRef.current = ev.point;
          moveEndPoint(ev.point, isShiftPressed);
          break;
        case InteractionState.SecondPointPlaced:
          checkHoveringStarted(ev.point);
          break;
        case InteractionState.HoveringEndPoint:
          checkHoveringEnded(endPoint, ev.point);
          break;
        case InteractionState.HoveringStartPoint:
          checkHoveringEnded(startPoint, ev.point);
          break;
        case InteractionState.MovingEndPoint:
          lastCursorRef.current = ev.point;
          moveEndPoint(ev.point, isShiftPressed);
          break;
        case InteractionState.MovingStartPoint:
          lastCursorRef.current = ev.point;
          moveStartPoint(ev.point, isShiftPressed);
          break;
      }
    },
    [
      interactionState,
      startPoint,
      isPointerDown,
      endPoint,
      moveEndPoint,
      moveStartPoint,
      onPointerDown,
      onPointerUp,
      checkHoveringStarted,
      checkHoveringEnded,
      isShiftPressed,
    ],
  );

  // Recompute the snapped endpoint when Shift toggles while actively drawing or dragging.
  // Using a non-exhaustive effect here, because we don't want the points to be adjusted at any other time point.
  useNonExhaustiveEffect(() => {
    const cursor = lastCursorRef.current;
    if (!cursor) return;

    switch (interactionState) {
      case InteractionState.FirstPointPlaced:
      case InteractionState.MovingEndPoint: {
        if (!startPoint) return;
        const { adjustedEnd } = computeMeasureEnd(
          startPoint,
          cursor,
          camera,
          isShiftPressed,
        );
        setEndPoint(adjustedEnd);
        break;
      }
      case InteractionState.MovingStartPoint: {
        if (!endPoint) return;
        const { adjustedEnd } = computeMeasureEnd(
          endPoint,
          cursor,
          camera,
          isShiftPressed,
        );
        setStartPoint(adjustedEnd);
        break;
      }
    }
  }, [isShiftPressed]);

  const onMeasureRemoved = useCallback(() => {
    lastCursorRef.current = null;
    setStartPoint(undefined);
    setEndPoint(undefined);
    setInteractionState(InteractionState.NoPointPlaced);
  }, []);

  const onUndoMeasureRemoval = useCallback(() => {
    setStartPoint(startPoint);
    setEndPoint(endPoint);
    setInteractionState(InteractionState.SecondPointPlaced);
  }, [startPoint, endPoint]);

  return {
    startPoint,
    endPoint,
    onPointerDown,
    onPointerUp,
    onPointerMove,
    onMeasureRemoved,
    onUndoMeasureRemoval,
    controlsEnabled,
    isDraggingMeasureLine:
      interactionState === InteractionState.MovingEndPoint ||
      interactionState === InteractionState.MovingStartPoint,
  };
}

enum InteractionState {
  NoPointPlaced = "noPointPlaced",
  FirstPointPlaced = "firstPointPlaced",
  SecondPointPlaced = "secondPointPlaced",
  HoveringStartPoint = "hoveringStartPoint",
  HoveringEndPoint = "hoveringEndPoint",
  MovingStartPoint = "movingStartPoint",
  MovingEndPoint = "movingEndPoint",
}

/**
 * Compute the end point of the line.
 *
 * @param start point of the line
 * @param cursor position to draw the line towards
 * @param camera camera used to evaluate screen-aligned snap axes
 * @param snapToAxis if true, lock line to horizontal/vertical axis
 * @returns the updated end point of the line
 */
function computeMeasureEnd(
  start: Vector3,
  cursor: Vector3,
  camera: OrthographicCamera,
  snapToAxis: boolean,
): { adjustedEnd: Vector3 } {
  if (!snapToAxis) {
    return { adjustedEnd: cursor.clone() };
  }

  const cameraUp = new Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
  const cameraRight = new Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
  const relativeStart = start.clone().applyMatrix4(camera.matrixWorldInverse);
  const relativeCursor = cursor.clone().applyMatrix4(camera.matrixWorldInverse);
  const displacement = cursor.clone().sub(start);

  const xDiff = Math.abs(relativeCursor.x - relativeStart.x);
  const yDiff = Math.abs(relativeCursor.y - relativeStart.y);
  const snapVertical = yDiff > xDiff;

  const adjustedEnd = snapVertical
    ? start.clone().add(cameraUp.multiplyScalar(displacement.dot(cameraUp)))
    : start
        .clone()
        .add(cameraRight.multiplyScalar(displacement.dot(cameraRight)));

  return { adjustedEnd };
}

/** Hovering threshold in pixels */
const HOVERING_THRESHOLD = 12;

/**
 * Check whether 3D points A and B are close enough at the current camera and resolution to issue an hovering state.
 *
 * @param A First point, 3D coordinates
 * @param B Second point, 3D coordinates
 * @param camera current camera
 * @param sizeY current viewport height
 * @returns whether point A is close enough to B at the current camera and resolution to issue an hovering state.
 */
function isHovering(
  A: Vector3,
  B: Vector3,
  camera: OrthographicCamera,
  sizeY: number,
): boolean {
  const distanceMeters = Math.max(Math.abs(A.x - B.x) + Math.abs(A.z - B.z));
  const distancePixels =
    (distanceMeters * sizeY * camera.zoom) / (camera.top - camera.bottom);
  return distancePixels < HOVERING_THRESHOLD;
}
